<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import FileUpload, { type FileUploadSelectEvent } from 'primevue/fileupload';
import TimesIcon from '@primeicons/vue/times';
import { useToastNotifications } from '@apparatix/composables';
import { Section, Stack, Toolbar, Typography } from './core';
import { FileExtensionToMimeType } from '@apparatix/constants/FileExtensionToMimeType';

enum SizeUnits {
  Kilobyte = 1000,
  Megabyte = 1000000,
}

type Props = {
  maxFiles?: number;
  maxTotalSizeMb?: number;
  maxIndividualSizeMb?: number;
  disabled?: boolean;
  showToastOnError?: boolean;
  /**
   * Passed through to the native file input (e.g. 'application/pdf').
   * Selected files are also checked against it, since drag-and-drop and
   * some browsers ignore the accept attribute.
   */
  accept?: string;
  chooseLabel?: string;
}

type Emits = {
  (e: 'file-added', file: File): void;
  (e: 'file-removed', file?: File): void;
  (e: 'error'): void;
}

const props = withDefaults(defineProps<Props>(), {
  maxFiles: undefined,
  maxTotalSizeMb: 150,
  maxIndividualSizeMb: undefined,
  showToastOnError: true,
  accept: undefined,
  chooseLabel: undefined,
});

const emit = defineEmits<Emits>();

const model = defineModel<File[]>();

const { showError } = useToastNotifications();

const fileCountReached = computed(() => props.maxFiles ? model.value?.length >= props.maxFiles : false);

/**
 * Human wording for the accept list, e.g. 'application/pdf,image/*' → 'PDF or image'.
 * Users never see a MIME type.
 */
const acceptedTypesLabel = computed(() => {
  if (!props.accept) {
    return '';
  }

  const labels = props.accept.split(',').map(a => a.trim().toLowerCase()).filter(Boolean).map(accepted => {
    if (accepted.startsWith('.')) {
      return accepted.slice(1).toUpperCase();
    }
    if (accepted.endsWith('/*')) {
      return accepted.slice(0, -2);
    }
    const extension = Object.entries(FileExtensionToMimeType).find(([, mime]) => mime === accepted)?.[0];

    return extension ? extension.toUpperCase() : accepted.split('/').pop().toUpperCase();
  });
  const unique = [...new Set(labels)];

  return unique.length > 1 ? `${unique.slice(0, -1).join(', ')} or ${unique.at(-1)}` : unique[0];
});

const invalidTypeMessage = (fileName: string) => (acceptedTypesLabel.value
  ? `${fileName} is not a ${acceptedTypesLabel.value} file. Choose a ${acceptedTypesLabel.value} file to continue.`
  : `${fileName} is not an accepted file type.`);

const getTotalFileSize = () => model.value?.reduce((acc, current) => current.size + acc, 0);

const matchesAccept = (file: File) => {
  if (!props.accept) {
    return true;
  }

  const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;

  return props.accept.split(',').map(a => a.trim().toLowerCase()).some(accepted => {
    if (accepted.startsWith('.')) {
      return extension === accepted;
    }
    if (accepted.endsWith('/*')) {
      return file.type.toLowerCase().startsWith(accepted.slice(0, -1));
    }
    return file.type.toLowerCase() === accepted;
  });
};

const canAddFile = (file: File) => {
  if (!matchesAccept(file)) {
    if (props.showToastOnError) {
      showError(invalidTypeMessage(file.name), 'Unable to add file', undefined, true);
    }
    return false;
  }

  if (props.maxIndividualSizeMb) {
    const allowedFileSize = props.maxIndividualSizeMb * SizeUnits.Megabyte;
    if (file.size > allowedFileSize) {
      if (props.showToastOnError) {
        showError(
          `${file.name} is ${(file.size / SizeUnits.Megabyte).toFixed(2)}mb, which exceeds the individual file size limit of ${props.maxIndividualSizeMb}mb`,
          'Unable to add file',
          undefined,
          true,
        );
      }
      return false;
    }
  }

  if (!props.maxTotalSizeMb) {
    return true;
  }

  const maxAllowedBytes = props.maxTotalSizeMb * SizeUnits.Megabyte;
  const currentSize = getTotalFileSize();
  const sizeAfterAdd = currentSize + file.size;

  if (sizeAfterAdd > maxAllowedBytes) {
    showError(`${file.name} exceeds the total upload size limit of ${props.maxTotalSizeMb}mb`, 'Unable to add file', undefined, true);

    return false;
  }

  return true;
};

const onFileSelect = (e: FileUploadSelectEvent) => {
  if (!model.value) {
    model.value = [];
  }

  (e.files as File[]).forEach(file => {
    if (!canAddFile(file)) {
      emit('error');
      return;
    }

    if (!model.value.find(f => f.name === file.name)) {
      model.value.push(file);
      emit('file-added', file);
    }
  });
};

const removeFile = (idx: number) => {
  emit('file-removed', model.value[idx]);
  model.value.splice(idx, 1);
};

const getFileSize = (size: number) => {
  if (size < SizeUnits.Kilobyte) {
    return `${size} b`;
  }

  if (size === SizeUnits.Kilobyte) {
    return '1 kb';
  }

  if (size < SizeUnits.Megabyte) {
    const kb = size / SizeUnits.Kilobyte;
    return `${kb.toFixed(2)} kb`;
  }

  if (size === SizeUnits.Megabyte) {
    return '1 mb';
  }

  const mb = size / SizeUnits.Megabyte;

  return `${mb.toFixed(2)} mb`;
};
</script>
<template>
  <Section variant="lite">
    <Stack :gap="4">
      <Toolbar align="left">
        <FileUpload
          mode="basic"
          :disabled="fileCountReached || props.disabled"
          :accept="props.accept"
          :choose-label="props.chooseLabel"
          :invalid-file-type-message="invalidTypeMessage('{0}')"
          custom-upload
          auto
          @select="onFileSelect"
        />
        <Typography v-if="model?.length > 0 && !fileCountReached" variant="section-subheading" class="ml-2">
          {{ model.length }} {{ model.length === 1 ? 'file' : 'files' }} selected
        </Typography>
        <Message v-if="fileCountReached" severity="warn" class="ml-2">
          Limit of {{ props.maxFiles }} {{ props.maxFiles === 1 ? 'file' : 'files' }} has been reached
        </Message>
      </Toolbar>
      <div
        v-for="(file, idx) in model"
        :key="file.name"
        class="flex items-center mt-2"
        :class="{'border-b border-[var(--p-content-border-color)] pb-2': idx !== model.length - 1}"
      >
        <div class="grow flex flex-col gap-1">
          <Typography variant="small-body">
            {{ file.name }}
          </Typography>
          <Typography variant="small-body" muted>
            {{ getFileSize(file.size) }}
          </Typography>
        </div>
        <div class="grow-0">
          <Button
            icon-only
            severity="danger"
            variant="text"
            rounded
            @click="removeFile(idx)"
          >
            <TimesIcon />
          </Button>
        </div>
      </div>
    </Stack>
  </Section>
</template>
